import React, { useState } from 'react';
const INK = '#01332b';
const BLUSH = '#E2725B';
export type HairStyle = 'sideParted' | 'wavyLong' | 'curlyAfro' | 'topKnot' | 'shortCrop';
export type MouthStyle = 'closed' | 'smile' | 'grin' | 'smirk';
export type PortraitMotif = 'none' | 'glasses' | 'sparkles' | 'heat' | 'ring';
interface CartoonPortraitProps {
  /** Accessible description of the character. */
  label: string;
  /** Saturated circle behind the bust. */
  disc: string;
  skin: string;
  skinShade: string;
  hair: string;
  shirt: string;
  hairStyle: HairStyle;
  mouth?: MouthStyle;
  motif?: PortraitMotif;
  /** Head tilt in degrees — small values only. */
  tilt?: number;
  className?: string;
}
let instance = 0;

/**
 * Flat, friendly cartoon bust portrait sitting inside a coloured disc.
 * One shared drawing so every family member is unmistakably related.
 */
export function CartoonPortrait({
  label,
  disc,
  skin,
  skinShade,
  hair,
  shirt,
  hairStyle,
  mouth = 'smile',
  motif = 'none',
  tilt = 0,
  className = ''
}: CartoonPortraitProps) {
  const [uid] = useState(() => `portrait-${instance += 1}`);
  return <svg viewBox="0 0 120 120" className={className} role="img" aria-label={label} data-id="element-2" data-mp="af8s76orzdyi">
      <defs data-id="element-3" data-mp="fgocgwj4pgfu">
        <clipPath id={uid} data-id="element-4" data-mp="fgxyya4yt5gs">
          <circle cx="60" cy="60" r="58" data-id="element-5" data-mp="fdk2fiizfqh9" />
        </clipPath>
      </defs>

      <circle cx="60" cy="60" r="58" fill={disc} data-id="element-6" data-mp="ke7pjt43bn67" />

      <g clipPath={`url(#${uid})`} data-id="element-7" data-mp="3iwlnf7p46sf8">
        {/* shoulders and tee */}
        <path d="M60 84 C 38 84 22 96 14 132 H106 C 98 96 82 84 60 84 Z" fill={shirt} data-id="element-8" data-mp="3iu8yuboupjt7" />
        <path d="M30 128 c2 -16 8 -26 16 -31" fill="none" stroke={INK} strokeWidth="1.2" opacity="0.3" strokeLinecap="round" data-id="element-9" data-mp="3nvpy62rosy7o" />
        <path d="M90 128 c-2 -16 -8 -26 -16 -31" fill="none" stroke={INK} strokeWidth="1.2" opacity="0.3" strokeLinecap="round" data-id="element-10" data-mp="1h6im2qz7czm" />

        <g transform={`rotate(${tilt} 60 84)`} data-id="element-11" data-mp="353451q85zlqs">
          {hairStyle === 'wavyLong' && <path d="M60 15 C 41 15 31 28 31 48 C 31 64 27 78 24 92 C 33 88 40 90 45 95 C 40 78 43 62 43 50 C 43 34 49 29 60 29 C 71 29 77 34 77 50 C 77 62 80 78 75 95 C 80 90 87 88 96 92 C 93 78 89 64 89 48 C 89 28 79 15 60 15 Z" fill={hair} data-id="element-12" data-mp="2x2oct9sgbb38" />}

          {/* neck */}
          <path d="M52 64 h16 v22 q-8 5 -16 0 z" fill={skinShade} data-id="element-13" data-mp="31x28zeextm8y" />

          {/* head */}
          <ellipse cx="60" cy="46" rx="21" ry="24" fill={skin} data-id="element-14" data-mp="2e7hf40h8x4kw" />
          <circle cx="39.6" cy="50" r="4.6" fill={skinShade} data-id="element-15" data-mp="297d954dyrs8x" />
          <circle cx="80.4" cy="50" r="4.6" fill={skinShade} data-id="element-16" data-mp="2o3b1ebkrocfv" />

          <Hair style={hairStyle} hair={hair} data-id="element-17" data-mp="2o4vwq3ewnzes" />

          {/* brows */}
          <path d="M46.5 39.5 q5 -3.6 9.6 -0.6" fill="none" stroke={INK} strokeWidth="2.6" strokeLinecap="round" data-id="element-18" data-mp="2j3ons1110u6j" />
          <path d="M63.9 38.9 q4.6 -3 9.6 0.6" fill="none" stroke={INK} strokeWidth="2.6" strokeLinecap="round" data-id="element-19" data-mp="3ukg02vvyfnl5" />

          {/* eyes */}
          <ellipse cx="51.5" cy="47" rx="2.3" ry="3" fill={INK} data-id="element-20" data-mp="axw6zw9l5bty" />
          <ellipse cx="68.5" cy="47" rx="2.3" ry="3" fill={INK} data-id="element-21" data-mp="3t2r3i1agp4oz" />

          {/* nose */}
          <path d="M59 42 c-3 7 -3 10 1.6 11" fill="none" stroke={INK} strokeWidth="1.5" strokeLinecap="round" opacity="0.75" data-id="element-22" data-mp="yowrhy5dxb2" />

          {/* cheeks */}
          <ellipse cx="46.6" cy="55" rx="4.2" ry="2.8" fill={BLUSH} opacity="0.32" data-id="element-23" data-mp="tmayslrq8tvt" />
          <ellipse cx="73.4" cy="55" rx="4.2" ry="2.8" fill={BLUSH} opacity="0.32" data-id="element-24" data-mp="fwhb46rykfno" />

          <Mouth style={mouth} data-id="element-25" data-mp="jxaxuwtnzoez" />

          {motif === 'glasses' && <g fill="none" stroke={INK} strokeWidth="2" strokeLinecap="round" data-id="element-26" data-mp="jvgazcyu1m89">
              <rect x="43.5" y="41" width="16" height="12" rx="6" data-id="element-27" data-mp="17lkhpt7bi23h" />
              <rect x="60.5" y="41" width="16" height="12" rx="6" data-id="element-28" data-mp="1ce3puan8k50w" />
              <path d="M59.5 46 h1.5" data-id="element-29" data-mp="3kuws1t6huube" />
              <path d="M43.5 45 l-4.5 1.5" data-id="element-30" data-mp="3kvy0dpw4l2pf" />
              <path d="M76.5 45 l4.5 1.5" data-id="element-31" data-mp="3ksjzyn70v3km" />
            </g>}
        </g>

        {/* neckline sits over the neck so the bust reads cleanly */}
        <path d="M44 85 q16 14 32 0 z" fill={shirt} data-id="element-32" data-mp="3axs01cpyw4mo" />
        <path d="M44 85 q16 14 32 0" fill="none" stroke={INK} strokeWidth="1.4" opacity="0.35" strokeLinecap="round" data-id="element-33" data-mp="36v55h41ww5ls" />

        {motif === 'sparkles' && <g fill="#FFFFFF" opacity="0.85" data-id="element-34" data-mp="2gf6dg5a0y82">
            <path d="M16 36 l2.6 5.4 5.4 2.6 -5.4 2.6 -2.6 5.4 -2.6 -5.4 -5.4 -2.6 5.4 -2.6 z" data-id="element-35" data-mp="2hp587jyeafr" />
            <path d="M100 24 l2 4.2 4.2 2 -4.2 2 -2 4.2 -2 -4.2 -4.2 -2 4.2 -2 z" data-id="element-36" data-mp="3ttrx31xsxyd4" />
            <path d="M104 66 l1.6 3.4 3.4 1.6 -3.4 1.6 -1.6 3.4 -1.6 -3.4 -3.4 -1.6 3.4 -1.6 z" data-id="element-37" data-mp="3pt81w3wsh4tm" />
          </g>}
        {motif === 'heat' && <g fill="none" stroke="#FFFFFF" strokeWidth="2.6" strokeLinecap="round" opacity="0.8" data-id="element-38" data-mp="lfszxwz3g6y1">
            <path d="M18 66 c6 -7 -6 -13 0 -20" data-id="element-39" data-mp="242j8dxwl7hmw" />
            <path d="M27 50 c6 -7 -6 -13 0 -20" data-id="element-40" data-mp="243uhmd8sxhle" />
            <path d="M102 66 c-6 -7 6 -13 0 -20" data-id="element-41" data-mp="291ckblw870ky" />
            <path d="M93 50 c-6 -7 6 -13 0 -20" data-id="element-42" data-mp="2925u6vqhe67b" />
          </g>}
        {motif === 'ring' && <circle cx="60" cy="60" r="50" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="4 8" strokeLinecap="round" opacity="0.55" data-id="element-43" data-mp="2d1o6520br6y0" />}
      </g>
    </svg>;
}
function Hair({
  style,
  hair
}: {
  style: HairStyle;
  hair: string;
}) {
  if (style === 'sideParted') {
    return <path d="M38 46 C 37 25 47 17 60 17 C 74 17 83 26 83 46 C 79 33 70 29 58 31 C 49 32.5 42 37 38 46 Z" fill={hair} data-id="element-44" data-mp="2wtb8h6grwqfi" />;
  }
  if (style === 'shortCrop') {
    return <g fill={hair} data-id="element-45" data-mp="35psbfmezhi06">
        <path d="M38 48 C 36 26 46 16 60 16 C 73 16 84 25 83 45 C 79 36 73 33 65 33 C 55 33 44 37 38 48 Z" data-id="element-46" data-mp="3aqzl5r0osr9h" />
        <path d="M66 18 c7 2 11 8 11 15 c-3 -6 -7 -9 -13 -10 z" data-id="element-47" data-mp="1aogqmvbh79s8" />
      </g>;
  }
  if (style === 'curlyAfro') {
    return <g fill={hair} data-id="element-48" data-mp="15qooz9l149au">
        <ellipse cx="60" cy="26" rx="24" ry="15" data-id="element-49" data-mp="15rgmipy6sh3r" />
        <circle cx="38" cy="34" r="9" data-id="element-50" data-mp="1kf1p8ir5geb5" />
        <circle cx="82" cy="34" r="9" data-id="element-51" data-mp="1kg4ekm8tq8oy" />
        <circle cx="44" cy="19" r="9.5" data-id="element-52" data-mp="1fntg7ntqeuyi" />
        <circle cx="60" cy="14" r="10" data-id="element-53" data-mp="1fms0v75onmaz" />
        <circle cx="76" cy="19" r="9.5" data-id="element-54" data-mp="1flqi0gifdvip" />
        <circle cx="32" cy="44" r="6.4" data-id="element-55" data-mp="1tegejevkyiu2" />
        <circle cx="88" cy="44" r="6.4" data-id="element-56" data-mp="1tderec1ai5vz" />
      </g>;
  }
  if (style === 'topKnot') {
    return <g fill={hair} data-id="element-57" data-mp="1pdcushaew2nk">
        <circle cx="60" cy="13" r="9" data-id="element-58" data-mp="23ddxm2a8lk5o" />
        <path d="M39 46 C 38 24 48 18 60 18 C 73 18 82 25 81 46 C 77 34 69 31 60 31 C 50 31 43 35 39 46 Z" data-id="element-59" data-mp="23ccoi3cs77ql" />
        <path d="M38 40 c-4 4 -5 10 -4 15 c-3 -5 -3 -12 0 -17 z" data-id="element-60" data-mp="1yfm6hahf222j" />
        <path d="M82 40 c4 4 5 10 4 15 c3 -5 3 -12 0 -17 z" data-id="element-61" data-mp="8yuv3zpp3gnn" />
      </g>;
  }
  return <path d="M40 44 C 40 26 48 20 60 20 C 72 20 80 26 80 44 C 74 33 66 31 57 33 C 49 35 43 38 40 44 Z" fill={hair} data-id="element-62" data-mp="40jkvnvhn44w" />;
}
function Mouth({
  style
}: {
  style: MouthStyle;
}) {
  if (style === 'grin') {
    return <g data-id="element-63" data-mp="2klonrnx4449r">
        <path d="M51.5 57.5 c4 11 13 11 17 0 z" fill={INK} data-id="element-64" data-mp="2kleyk2jw6154" />
        <path d="M53.5 58.4 h13 c-1.5 3.2 -11 3.2 -13 0 z" fill="#FFFFFF" data-id="element-65" data-mp="25ydcln4uema6" />
      </g>;
  }
  if (style === 'smirk') {
    return <path d="M53.5 61 q7 2.5 12.5 -4.5" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round" data-id="element-66" data-mp="2avludmm2woaw" />;
  }
  if (style === 'closed') {
    return <path d="M54 60 q6 4 12 -1" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round" data-id="element-67" data-mp="21xtgmmw09opm" />;
  }
  return <path d="M52 58.5 q8 7.5 15.5 -1" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round" data-id="element-68" data-mp="1rx99x4ppvs00" />;
}